import moment from 'moment';

import { CSSColors } from 'Components/Colors';
import { Action, ActionStatus } from 'Models/Actions';
import { DashboardType, IssuesExceptionsDashboardType } from 'Models/Dashboards';
import { ExceptionResponse, ExceptionStatus } from 'Models/Exceptions';
import { IssueResponse, IssueStatus } from 'Models/Issues';
import { Effectiveness } from 'Models/OperationalControls';
import { ProductModule } from 'Models/ProductModule';

/** Returns the module whose issues and exceptions are displayed in the given tab of the Dashboards page. */
export const getProductModuleForDashboardType = (dashboardType: IssuesExceptionsDashboardType): ProductModule => {
    switch (dashboardType) {
        case DashboardType.CONTROL_AUTOMATION:
            return ProductModule.CONTROLS;
        case DashboardType.UCRM:
            return ProductModule.UCRM;
        case DashboardType.THIRD_PARTY_RISK_MANAGEMENT:
            return ProductModule.TPRM;
    }
};

/** The check for undefined herein is necessary for Actions because created_timestamp was added after the feature went live. The logic is also applied to Exceptions and Issues simply for consistency/simplicity, but is not technically necessary since created_timestamp has always been an attribute of those features. */
export const countByMonth = (entities: (Action | IssueResponse | ExceptionResponse)[], months: string[]): number[] => {
    return months.map((month) => {
        const currentMonthIssues = entities.filter((entity) => entity.created_timestamp !== undefined && moment(entity.created_timestamp).format('MMM YY') === month);
        return currentMonthIssues.length;
    });
};

export const countClosedActionsByMonth = (actions: Action[], months: string[]): number[] => {
    return months.map((month) => {
        const currentMonthActions = getClosedActionsPerMonth(actions, month);
        return currentMonthActions.length;
    });
};

export const countClosedExceptionsByMonth = (exceptions: ExceptionResponse[], months: string[]): number[] => {
    return months.map((month) => {
        const currentMonthExceptions = getClosedExceptionsPerMonth(exceptions, month);
        return currentMonthExceptions.length;
    });
};

export const countClosedIssuesByMonth = (issues: IssueResponse[], months: string[]): number[] => {
    return months.map((month) => {
        const currentMonthIssues = getClosedIssuesPerMonth(issues, month);
        return currentMonthIssues.length;
    });
};

export const countTotalActionsByMonth = (actions: Action[], months: string[]): number[] => {
    return months.map((month) => {
        const totalOpenedBeforeEnd = getTotalActionsPerMonth(actions, month);
        return totalOpenedBeforeEnd.length;
    });
};

export const countTotalExceptionsByMonth = (exceptions: ExceptionResponse[], months: string[]): number[] => {
    return months.map((month) => {
        const totalOpenedBeforeEnd = getTotalExceptionsPerMonth(exceptions, month);
        return totalOpenedBeforeEnd.length;
    });
};

export const countTotalIssuesByMonth = (issues: IssueResponse[], months: string[]): number[] => {
    return months.map((month) => {
        const totalOpenedBeforeEnd = getTotalIssuesPerMonth(issues, month);
        return totalOpenedBeforeEnd.length;
    });
};

/** The check for undefined herein is necessary because closed_timestamp was added to Actions after the feature went live. */
export const getClosedActionsPerMonth = (actions: Action[], month: string) => {
    return actions.filter((action) => action.status === ActionStatus.CLOSED && action.closed_timestamp !== undefined && moment(action.closed_timestamp).format('MMM YY') === month);
};

/** The check for undefined herein is NOT technically because closed_timestamp has always been present for Exceptions. The logic is included here simply to be consistent with the logic for Actions, where it is necessary. */
export const getClosedExceptionsPerMonth = (exceptions: ExceptionResponse[], month: string) => {
    return exceptions.filter((exception) => exception.status === ExceptionStatus.CLOSED && exception.closed_timestamp !== undefined && moment(exception.closed_timestamp).format('MMM YY') === month);
};

/** The check for undefined herein is NOT technically because closed_timestamp has always been present for Issues. The logic is included here simply to be consistent with the logic for Actions, where it is necessary. */
export const getClosedIssuesPerMonth = (issues: IssueResponse[], month: string) => {
    return issues.filter((issue) => issue.status === IssueStatus.CLOSED && issue.closed_timestamp !== undefined && moment(issue.closed_timestamp).format('MMM YY') === month);
};

export const getEffectivenessChartColor = (riskRating: Effectiveness): CSSColors => {
    switch (riskRating) {
        case Effectiveness.UNRATED:
            return CSSColors.DARK_GRAY;
        case Effectiveness.FAIL:
            return CSSColors.RED;
        case Effectiveness.WEAK:
            return CSSColors.ORANGE;
        case Effectiveness.MODERATE:
            return CSSColors.YELLOW;
        case Effectiveness.STRONG:
            return CSSColors.LIGHT_GREEN;
        case Effectiveness.ROBUST:
            return CSSColors.DARK_GREEN;
    }
};

export const getMonths = (startDate: Date, endDate: Date): string[] => {
    const months = [];

    // create a copy to avoid mutating original date
    const currentDate = moment(startDate);
    const end = moment(endDate).endOf('M');

    while (currentDate.isBefore(end)) {
        months.push(currentDate.format('MMM YY'));
        currentDate.add(1, 'month');
    }

    return months;
};

export const getOpenActionsPerMonth = (actions: Action[], month: string) => {
    return actions.filter((action) => action.due_date && moment(action.due_date).format('MMM YY') === month);
};

export const getOpenExceptionsPerMonth = (exceptions: ExceptionResponse[], month: string) => {
    return exceptions.filter((exception) => exception.expiration_date && moment(exception.expiration_date).format('MMM YY') === month);
};

export const getOpenIssuesPerMonth = (issues: IssueResponse[], month: string) => {
    return issues.filter((issue) => issue.due_date && moment(issue.due_date).format('MMM YY') === month);
};

/** The checks for undefined herein are necessary for Actions because created_timestamp and closed_timestamp were added after the feature went live. */
export const getTotalActionsPerMonth = (actions: Action[], month: string): Action[] => {
    return actions.filter((action) => {
        const isOpened = action.created_timestamp !== undefined && moment(action.created_timestamp).isBefore(moment(month, 'MMM YY').endOf('M'));
        if (action.status === ActionStatus.CLOSED) {
            return isOpened && action.closed_timestamp !== undefined && !moment(action.closed_timestamp).isBefore(moment(month, 'MMM YY').endOf('M'));
        } else {
            return isOpened;
        }
    });
};

/** The checks for undefined herein are NOT technically necessary for Exceptions because created_timestamp and closed_timestamp have always been present on that feature. The logic is included here simply to be consistent with the logic for Actions, where it is necessary. */
export const getTotalExceptionsPerMonth = (exceptions: ExceptionResponse[], month: string) => {
    return exceptions.filter((exception) => {
        const isOpened = exception.created_timestamp !== undefined && moment(exception.created_timestamp).isBefore(moment(month, 'MMM YY').endOf('M'));
        if (exception.status === ExceptionStatus.CLOSED) {
            return isOpened && exception.closed_timestamp !== undefined && !moment(exception.closed_timestamp).isBefore(moment(month, 'MMM YY').endOf('M'));
        } else {
            return isOpened;
        }
    });
};

/** The checks for undefined herein are NOT technically necessary for Issues because created_timestamp and closed_timestamp have always been present on that feature. The logic is included here simply to be consistent with the logic for Actions, where it is necessary. */
export const getTotalIssuesPerMonth = (issues: IssueResponse[], month: string): IssueResponse[] => {
    return issues.filter((issue) => {
        const isOpened = issue.created_timestamp !== undefined && moment(issue.created_timestamp).isBefore(moment(month, 'MMM YY').endOf('M'));
        if (issue.status === IssueStatus.CLOSED) {
            return isOpened && issue.closed_timestamp !== undefined && !moment(issue.closed_timestamp).isBefore(moment(month, 'MMM YY').endOf('M'));
        } else {
            return isOpened;
        }
    });
};
